"use client"

import DashboardContentWrapper from "../../../../shared/components/dashboard-content-wrapper"
import { Button } from "../../../../shared/components/ui/button"
import Heading from "../../../../shared/components/ui/typography/heading"

import { DASHBOARD_ROUTE } from "../../../../shared/constants/pages-routes"
import { buildRegionAwareHref } from "../../../../shared/lib/utils"
import WorkOrdersTablePagination from "@/features/work-orders/components/work-orders-table-pagination"
import { Plus } from "lucide-react"
import Link from "next/link"
import React from "react"
import WorkOrdersCardsMobile from "@/features/work-orders/components/work-orders-cards-mobile"
import PaginationDataContextProvider from "@/features/work-orders/context/pagination-data-context"
import WorkOrdersList from "@/features/work-orders/components/work-orders-list"
import WorkOrdersSearch from "@/features/work-orders/components/work-orders-search"
import WorkOrdersFilterButton from "@/features/work-orders/components/work-orders-filter-button"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import { useRegion } from "../../../../shared/hooks/use-region"
import LocationSelector from "../../../../shared/components/location-selector"
import ScrollToTopFab from "@/shared/components/scroll-to-top-fab"

function WorkOrdersPage() {
    const { session } = useSession()
    const { regionSlug } = useRegion()
    const createHref = React.useMemo(() =>
        buildRegionAwareHref(regionSlug, DASHBOARD_ROUTE.createWorkOrders),
    [regionSlug])

    // Check if user is Admin or BaseLeader
    const canCreateWorkOrder =
        session?.role === UserRole.ADMIN ||
        session?.role === UserRole.BASE_LEADER

    // Privacy: hide filters for Volunteer, Homeowner, Supporter (search only)
    const hideFilters = [
        UserRole.VOLUNTEER,
        UserRole.HOMEOWNER,
        UserRole.Supporter,
    ].includes(session?.role as UserRole)

    return (
        <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
            <PaginationDataContextProvider>
                <div className="flex flex-col gap-4 md:flex-row md:justify-between">
                    <Heading>Work Orders</Heading>
                    <LocationSelector />
                </div>
                {/* table wrapper */}
                <div className="border rounded px-3 py-5 space-y-4 mb-10 hidden xl:block">
                    <div className='flex flex-col gap-4'>
                        {/* Desktop: filter/search (center), Create Work Order (right) */}
                        <div className='grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center'>
                            {/* Center: Filter and Search (Filter hidden for Volunteer, Homeowner, Supporter) */}
                            <div className="flex gap-4 items-center justify-center min-w-0">
                                {!hideFilters && <WorkOrdersFilterButton />}
                                <div className="flex-1 w-full">
                                    <WorkOrdersSearch />
                                </div>
                            </div>
                            
                            {/* Right: Create Work Order - Visible to Admin and Base Leader users */}
                            {canCreateWorkOrder && (
                                <div className="flex items-center justify-end">
                                    <Button asChild className="bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10">
                                        <Link href={createHref}>
                                            <Plus className="w-5 h-5" />
                                            Create Work Order
                                        </Link>
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                    <WorkOrdersList />
                    <WorkOrdersTablePagination />
                </div>
                <div className="block xl:hidden">
                    <WorkOrdersCardsMobile />
                    <div className="mt-6">
                        <WorkOrdersTablePagination />
                    </div>
                </div>
                <ScrollToTopFab />
            </PaginationDataContextProvider>
        </DashboardContentWrapper>
    )
}

export default WorkOrdersPage

